import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  IconButton,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Code,
  Alert,
  AlertIcon,
  useColorModeValue,
  Divider,
  Spinner,
} from '@chakra-ui/react';
import { FiCode, FiEye, FiSave, FiRefreshCw, FiZap } from 'react-icons/fi';
import { generateCSSAI, AIGenerateCSSReq } from '../../services/ai';
import { ELEMENT_TSX_CONTEXT } from './elementContext';

interface CustomCSSEditorProps {
  elementName: string;
  onCSSChange: (css: string) => void;
  currentCSS?: string;
  currentStyles?: Record<string, any>;
  theme?: Record<string, string>;
}

const CustomCSSEditor: React.FC<CustomCSSEditorProps> = ({
  elementName,
  onCSSChange,
  currentCSS = '',
  currentStyles = {},
  theme = {},
}) => {
  const [css, setCSS] = useState(currentCSS);
  const [isValid, setIsValid] = useState(true);
  const [preview, setPreview] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('Zvýrazni tento blok: moderní vzhled, zaoblené rohy, stín, lepší hover efekt; respektuj klubové barvy a responzivitu.');
  const [aiLoading, setAILoading] = useState(false);
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    setCSS(currentCSS);
  }, [currentCSS]);

  const validateCSS = (cssString: string): boolean => {
    try {
      // Basic CSS validation
      if (!cssString.trim()) return true;
      
      // Check for balanced braces
      const openBraces = (cssString.match(/{/g) || []).length;
      const closeBraces = (cssString.match(/}/g) || []).length;
      
      return openBraces === closeBraces;
    } catch {
      return false;
    }
  };

  // Collect rich AI context about the current element and page layout
  const collectAIContext = (elName: string) => {
    try {
      const rootSelector = `[data-element="${elName}"]`;
      const el = document.querySelector(rootSelector) as HTMLElement | null;
      const container = (document.querySelector('.myuibrix-viewport-wrapper') as HTMLElement) || (document.querySelector('.container') as HTMLElement) || null;

      const cssVars = (() => {
        const style = getComputedStyle(document.documentElement);
        const keys = ['--primary','--primary-light','--secondary','--text','--bg','--bg-soft','--club-primary','--club-text-on-primary'];
        const out: Record<string,string> = {};
        keys.forEach(k => { const v = style.getPropertyValue(k); if (v) out[k] = v.trim(); });
        return out;
      })();

      const elComputed = el ? getComputedStyle(el) : null;
      const computed = elComputed ? {
        display: elComputed.display,
        gridTemplateColumns: elComputed.gridTemplateColumns,
        gridTemplateRows: elComputed.gridTemplateRows,
        gap: elComputed.gap,
        justifyItems: (elComputed as any).justifyItems,
        alignItems: (elComputed as any).alignItems,
        color: elComputed.color,
        backgroundColor: elComputed.backgroundColor,
        padding: `${elComputed.paddingTop} ${elComputed.paddingRight} ${elComputed.paddingBottom} ${elComputed.paddingLeft}`,
        margin: `${elComputed.marginTop} ${elComputed.marginRight} ${elComputed.marginBottom} ${elComputed.marginLeft}`,
        fontFamily: elComputed.fontFamily,
        fontSize: elComputed.fontSize,
      } : {};

      const rect = el ? el.getBoundingClientRect() : null;
      const neighborInfo = (() => {
        try {
          const nodes = Array.from((container || document).querySelectorAll('[data-element]')) as HTMLElement[];
          const names = nodes.map(n => n.getAttribute('data-element')) as (string|null)[];
          const index = names.findIndex(n => n === elName);
          const prev = index > 0 ? names[index-1] : null;
          const next = index >= 0 && index < names.length - 1 ? names[index+1] : null;
          return { index, total: names.length, previous: prev, next };
        } catch { return {}; }
      })();

      const containerComputed = container ? getComputedStyle(container) : null;
      const containerInfo = containerComputed ? {
        display: containerComputed.display,
        gridTemplateColumns: containerComputed.gridTemplateColumns,
        gridAutoFlow: containerComputed.gridAutoFlow,
        gap: containerComputed.gap,
      } : {};

      // HTML snapshot (truncate to keep payload small)
      const rootHtml = el ? el.outerHTML.slice(0, 6000) : '';

      const tsxCtx = ELEMENT_TSX_CONTEXT[elName] || {};
      return {
        page_path: typeof window !== 'undefined' ? window.location.pathname : '',
        element: {
          name: elName,
          variant: el?.getAttribute('data-variant') || null,
          classList: el ? Array.from(el.classList) : [],
          attributes: el ? Array.from(el.attributes).map(a => ({ name: a.name, value: a.value })) : [],
          rect: rect ? { width: rect.width, height: rect.height } : undefined,
          computed,
          root_html_snapshot: rootHtml,
        },
        container: containerInfo,
        neighbors: neighborInfo,
        css_variables: cssVars,
        tsx_snippet: tsxCtx.tsx || undefined,
        known_selectors: tsxCtx.selectors || undefined,
        design_notes: tsxCtx.notes || undefined,
      };
    } catch {
      return {};
    }
  };

  const handleCSSChange = (value: string) => {
    setCSS(value);
    const valid = validateCSS(value);
    setIsValid(valid);
    
    if (valid && preview) {
      applyCSS(value);
    }
  };

  const applyCSS = (cssString: string) => {
    // Remove existing custom style
    const existingStyle = document.getElementById(`custom-css-${elementName}`);
    if (existingStyle) {
      existingStyle.remove();
    }

    if (cssString.trim()) {
      // Create new style element
      const style = document.createElement('style');
      style.id = `custom-css-${elementName}`;
      // If the CSS contains braces or at-rules, assume full CSS block already scoped
      const hasBlocks = /\{[^}]*\}|@media|@keyframes/.test(cssString);
      if (hasBlocks) {
        style.textContent = cssString;
      } else {
        // Treat as declarations, scope under element
        // Ensure each declaration is marked important to override theme CSS
        const importantDecls = cssString
          .split(';')
          .map(s => s.trim())
          .filter(Boolean)
          .map(s => {
            // Avoid double !important
            return /!important\s*$/.test(s) ? s : `${s} !important`;
          })
          .join(';\n            ');

        style.textContent = `
          [data-element="${elementName}"] {
            ${importantDecls};
          }
        `;
      }
      document.head.appendChild(style);
    }
  };

  const handleSave = () => {
    if (!isValid) {
      toast({
        title: 'Invalid CSS',
        description: 'Please fix CSS errors before saving',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    applyCSS(css);
    onCSSChange(css);
    
    toast({
      title: 'CSS Applied',
      description: 'Custom styles have been applied',
      status: 'success',
      duration: 2000,
    });
  };

  const handleReset = () => {
    setCSS('');
    const existingStyle = document.getElementById(`custom-css-${elementName}`);
    if (existingStyle) {
      existingStyle.remove();
    }
    onCSSChange('');
  };

  const cssExamples = [
    {
      label: 'Background Gradient',
      code: `background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
color: white;`,
    },
    {
      label: 'Shadow & Hover',
      code: `box-shadow: 0 10px 25px rgba(0,0,0,0.1);
transition: transform 0.3s;

&:hover {
  transform: translateY(-5px);
}`,
    },
    {
      label: 'Border Radius',
      code: `border-radius: 20px;
overflow: hidden;`,
    },
    {
      label: 'Animation',
      code: `animation: fadeIn 1s ease-in;

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}`,
    },
  ];

  return (
    <Box width="100%" height="100%">
      <Tabs size="sm" variant="enclosed" colorScheme="purple">
        <TabList>
          <Tab>
            <HStack spacing={2}>
              <FiCode />
              <Text>CSS Editor</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack spacing={2}>
              <FiEye />
              <Text>Examples</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack spacing={2}>
              <FiZap />
              <Text>AI (beta)</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Editor Tab */}
          <TabPanel p={0}>
            <VStack align="stretch" spacing={3} p={4}>
              <HStack justify="space-between">
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                  Custom CSS for {elementName}
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="xs"
                    leftIcon={<FiEye />}
                    variant={preview ? 'solid' : 'outline'}
                    colorScheme={preview ? 'blue' : 'gray'}
                    onClick={() => {
                      setPreview(!preview);
                      if (!preview && isValid) {
                        applyCSS(css);
                      }
                    }}
                  >
                    Preview
                  </Button>
                  <Button
                    size="xs"
                    leftIcon={<FiRefreshCw />}
                    variant="ghost"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </HStack>
              </HStack>

              {!isValid && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Invalid CSS syntax. Check for missing braces or semicolons.
                </Alert>
              )}

              <Textarea
                value={css}
                onChange={(e) => handleCSSChange(e.target.value)}
                placeholder={`/* Enter custom CSS properties */
background: #f0f0f0;
padding: 20px;
border-radius: 10px;`}
                fontFamily="monospace"
                fontSize="sm"
                minHeight="300px"
                bg={useColorModeValue('gray.50', 'gray.900')}
                borderColor={isValid ? borderColor : 'red.300'}
                _focus={{
                  borderColor: isValid ? 'purple.400' : 'red.400',
                  boxShadow: isValid ? '0 0 0 1px var(--chakra-colors-purple-400)' : '0 0 0 1px var(--chakra-colors-red-400)',
                }}
              />

              <Divider />

              <Alert status="info" borderRadius="md" fontSize="xs">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">Pro tip:</Text>
                  <Text>Use standard CSS properties. Avoid selectors - styles apply to the element automatically.</Text>
                </Box>
              </Alert>

              <Button
                leftIcon={<FiSave />}
                colorScheme="purple"
                size="sm"
                onClick={handleSave}
                isDisabled={!isValid}
              >
                Apply CSS
              </Button>
            </VStack>
          </TabPanel>

          {/* Examples Tab */}
          <TabPanel>
            <VStack align="stretch" spacing={3} p={4}>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                Quick CSS Examples
              </Text>
              
              {cssExamples.map((example, index) => (
                <Box
                  key={index}
                  p={3}
                  borderRadius="md"
                  border="1px"
                  borderColor={borderColor}
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: 'purple.400',
                    transform: 'translateX(4px)',
                  }}
                  onClick={() => setCSS(example.code)}
                >
                  <Text fontWeight="bold" fontSize="sm" mb={2}>
                    {example.label}
                  </Text>
                  <Code
                    fontSize="xs"
                    display="block"
                    whiteSpace="pre"
                    p={2}
                    borderRadius="sm"
                    bg={useColorModeValue('gray.100', 'gray.900')}
                  >
                    {example.code}
                  </Code>
                </Box>
              ))}
            </VStack>
          </TabPanel>

          {/* AI Tab */}
          <TabPanel>
            <VStack align="stretch" spacing={3} p={4}>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                Vygenerovat CSS pomocí AI
              </Text>

              <Textarea
                value={aiPrompt}
                onChange={(e) => setAIPrompt(e.target.value)}
                placeholder="Popište, jak má daný blok vypadat (česky). Např.: Tmavé pozadí, světlý text, zaoblené rohy, 2-sloupcový layout na desktopu, jeden sloupec na mobilu."
                fontSize="sm"
                minHeight="120px"
                bg={useColorModeValue('gray.50', 'gray.900')}
                borderColor={borderColor}
              />

              <HStack spacing={2} flexWrap="wrap">
                {[
                  'Tmavé pozadí a světlý text',
                  'Skleněný efekt (glassmorphism)',
                  'Zaoblené rohy a měkký stín',
                  'Dvou-sloupcový grid, mobil 1 sloupec',
                  'Akcent klubových barev',
                ].map((t, idx) => (
                  <Button key={idx} size="xs" variant="outline" onClick={() => setAIPrompt(p => `${p} ${t}.`)}>
                    {t}
                  </Button>
                ))}
              </HStack>

              <HStack>
                <Button
                  leftIcon={aiLoading ? <Spinner size="xs" /> : <FiZap />}
                  colorScheme="purple"
                  size="sm"
                  isLoading={aiLoading}
                  onClick={async () => {
                    try {
                      setAILoading(true);
                      const payload: AIGenerateCSSReq = {
                        prompt: aiPrompt,
                        element_name: elementName,
                        root_selector: `[data-element="${elementName}"]`,
                        current_css: css,
                        current_styles: currentStyles || {},
                        theme: (theme as any) || {},
                        breakpoints: [640, 960, 1200],
                        context: collectAIContext(elementName),
                      };
                      const res = await generateCSSAI(payload);
                      const next = (res?.css || '').trim();
                      if (!next) {
                        toast({ title: 'AI nevrátila CSS', status: 'warning', duration: 2500 });
                        return;
                      }
                      setCSS(next);
                      setIsValid(true);
                      setPreview(true);
                      applyCSS(next);
                      // Persist into parent editor state so it survives panel close
                      onCSSChange(next);
                      toast({ title: 'CSS vygenerováno', status: 'success', duration: 1500 });
                    } catch (e: any) {
                      toast({ title: 'Chyba při generování CSS', description: e?.message || 'Zkuste to znovu', status: 'error', duration: 3000 });
                    } finally {
                      setAILoading(false);
                    }
                  }}
                >
                  Vygenerovat CSS
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAIPrompt('')}>Vymazat zadání</Button>
              </HStack>

              <Alert status="info" borderRadius="md" fontSize="xs">
                <AlertIcon />
                AI výstup je automaticky scope-nutý pod `[data-element="název"]`. Po vygenerování se CSS předvyplní do editoru a lze ho dál upravovat.
              </Alert>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default CustomCSSEditor;
